import { useState } from "react";
import { ArrowRightLeft } from "lucide-react";
import { onField, subbedOut, sentOff, usedOpportunities, opportunitiesFor, maxOpportunities, isExtraTime } from "@/lib/events";
import { teamStyle } from "@/lib/colors";

const Num = ({ n, order, onClick, testId, tone, style }) => (
  <button data-testid={testId} onClick={onClick} style={style}
    className={`tap relative h-14 border font-display text-2xl font-bold ${order ? `ring-4 z-10 border-transparent ${tone === "out" ? "ring-[#FF3B30]" : "ring-[#34C759]"}` : "border-white/30 hover:ring-2 hover:ring-white/60"}`}>
    {n}
    {order && (
      <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full text-[11px] font-sans font-bold flex items-center justify-center text-white ${tone === "out" ? "bg-[#FF3B30]" : "bg-[#34C759]"}`}>
        {order}
      </span>
    )}
  </button>
);

const toggle = (list, n) => (list.includes(n) ? list.filter((x) => x !== n) : [...list, n]);

const TeamSub = ({ side, match, sel, onChange, halftime }) => {
  const field = onField(match, side);
  const out = subbedOut(match, side);
  const off = sentOff(match, side);
  const used = usedOpportunities(match, side);
  const free = opportunitiesFor(match).filter((o) => !used.has(o));
  const subCount = match.events.filter((e) => e.type === "substituicao" && e.team === side).length + sel.out.length;
  const maxSubs = isExtraTime(match) ? 6 : 5;
  const nums = match[side].players.map((p) => String(p.number));
  const bench = nums.filter((n) => !field.has(n) && !out.has(n) && !off.has(n));
  const grid = "grid grid-cols-5 sm:grid-cols-6 gap-2";
  const order = (list, n) => (list.includes(n) ? list.indexOf(n) + 1 : null);
  const mismatch = sel.out.length !== sel.in.length;
  const needOpp = !halftime && sel.out.length > 0 && free.length > 0 && !sel.opp;
  return (
    <div data-testid={`sub-team-${side}`} className={`flex-1 min-w-0 p-4 border ${sel.out.length || sel.in.length ? "border-white/40" : "border-white/10"}`}>
      <h3 className="font-display text-2xl font-bold uppercase truncate mb-3">{match[side].name}</h3>
      <p className="label-caps mb-2">Oportunidade de substituição · {used.size}/{maxOpportunities(match)} usadas</p>
      <p data-testid={`sub-${side}-count`} className={`text-xs mb-2 ${subCount > maxSubs ? "text-[#FF9F0A]" : "text-zinc-500"}`}>
        Substituições: {subCount}/{maxSubs}{isExtraTime(match) ? " (inclui +1 do prolongamento)" : ""}
      </p>
      {halftime && <p data-testid={`sub-${side}-halftime-note`} className="text-sm text-[#5AC8FA] mb-5">Ao intervalo — não conta como oportunidade</p>}
      <div data-testid={`sub-${side}-opps`} className={`flex flex-wrap gap-2 mb-5 ${halftime ? "hidden" : ""}`}>
        {free.map((o) => (
          <button key={o} data-testid={`sub-${side}-opp-${o}`} onClick={() => onChange({ ...sel, opp: sel.opp === o ? null : o })}
            className={`tap w-12 h-12 rounded-full border-2 font-display text-xl font-bold ${sel.opp === o ? "bg-[#CCFF00] border-[#CCFF00] text-black" : "border-white/30 hover:border-white/70"}`}>
            {o}
          </button>
        ))}
        {!free.length && <p data-testid={`sub-${side}-no-opps`} className="text-sm text-[#FF9F0A]">As {maxOpportunities(match)} oportunidades já foram utilizadas</p>}
      </div>
      <p className="label-caps mb-2 text-[#FF3B30]">Sai · {sel.out.length}</p>
      <div className={grid}>
        {nums.filter((n) => field.has(n)).map((n) => (
          <Num key={n} n={n} tone="out" style={teamStyle(match[side])} order={order(sel.out, n)} testId={`sub-${side}-out-${n}`} onClick={() => onChange({ ...sel, out: toggle(sel.out, n) })} />
        ))}
      </div>
      <p className="label-caps mt-5 mb-2 text-[#34C759]">Entra · {sel.in.length}</p>
      <div className={grid}>
        {bench.map((n) => (
          <Num key={n} n={n} tone="in" style={teamStyle(match[side])} order={order(sel.in, n)} testId={`sub-${side}-in-${n}`} onClick={() => onChange({ ...sel, in: toggle(sel.in, n) })} />
        ))}
      </div>
      {!bench.length && <p className="text-sm text-zinc-500">Sem suplentes disponíveis</p>}
      {mismatch && <p data-testid={`sub-${side}-mismatch`} className="text-sm text-[#FF9F0A] mt-3">Selecione o mesmo número de jogadores a sair e a entrar</p>}
      {needOpp && <p data-testid={`sub-${side}-need-opp`} className="text-sm text-[#FF9F0A] mt-3">Escolha a oportunidade de substituição</p>}
      {sel.out.length > 0 && !mismatch && (
        <p data-testid={`sub-${side}-pairs`} className="text-sm text-zinc-300 mt-3">
          {sel.out.map((o, i) => `${o} → ${sel.in[i]}`).join("  ·  ")}
        </p>
      )}
    </div>
  );
};

const EMPTY = { out: [], in: [], opp: null };

export const SubstitutionPicker = ({ match, onSave, halftimeDefault = false }) => {
  const [sel, setSel] = useState({ home: EMPTY, away: EMPTY });
  const [halftime, setHalftime] = useState(halftimeDefault);
  const sides = ["home", "away"];
  const total = sides.reduce((n, s) => n + sel[s].out.length, 0);
  const oppOk = (s) => halftime || !sel[s].out.length || sel[s].opp || usedOpportunities(match, s).size >= maxOpportunities(match);
  const ready = total > 0 && sides.every((s) => sel[s].out.length === sel[s].in.length && oppOk(s));
  const save = () =>
    onSave(sides.flatMap((s) => sel[s].out.map((o, i) => ({
      team: s, target_type: "player", target: o, player_in: sel[s].in[i], opportunity: halftime ? null : sel[s].opp || null, halftime: halftime || null,
    }))));
  return (
    <div>
      <p className="text-sm text-zinc-400 mb-4">Toque em vários jogadores para registar substituições múltiplas. Os números emparelham pela ordem em que os seleciona.</p>
      <label data-testid="sub-halftime-toggle" className="tap inline-flex items-center gap-3 mb-4 px-4 min-h-12 py-2 border border-[#5AC8FA]/50 cursor-pointer select-none">
        <input type="checkbox" checked={halftime} onChange={(e) => setHalftime(e.target.checked)} className="w-5 h-5 accent-[#5AC8FA]" />
        <span className="text-sm font-semibold">Substituição ao intervalo <span className="text-zinc-400 font-normal">(não conta como oportunidade)</span></span>
      </label>
      <div className="flex flex-col md:flex-row gap-4">
        {sides.map((s) => (
          <TeamSub key={s} side={s} match={match} sel={sel[s]} halftime={halftime} onChange={(v) => setSel({ ...sel, [s]: v })} />
        ))}
      </div>
      <div className="flex justify-end mt-6">
        <button data-testid="sub-save" disabled={!ready} onClick={save}
          className="tap h-14 px-8 bg-[#CCFF00] text-black font-display text-xl font-bold uppercase flex items-center gap-3 disabled:opacity-30">
          <ArrowRightLeft size={20} /> {ready ? `Registar ${total} substituiç${total > 1 ? "ões" : "ão"}` : "Escolha quem sai e quem entra"}
        </button>
      </div>
    </div>
  );
};
